<template>
	<div class="relative isolate overflow-visible w-full lg:max-w-container mx-auto">
		<div class="lg:hidden flex flex-col items-start gap-2 min-h-36 px-4 sm:pt-3 sm:px-6 mt-2 lg:-mt-16">
			<motion.div
				:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
				:initial="{ opacity: 0, transform: 'translateY(10px)' }"
				:transition="{ duration: 1, delay: 0.1 }"
				:in-view-options="{ once: true }"
				class="text-primary text-5xl sm:text-7xl tracking-tight font-semibold will-change-transform"
			>
				{{ $t('business.hero.title1') }} <br>
				<span class="md:whitespace-nowrap">
					{{ $t('business.hero.title2') }}
				</span>
			</motion.div>
		</div>
		<UPageHero
			reverse
			orientation="horizontal"
			:description="$t('business.hero.description')"
			:ui="{
				container: 'flex flex-col items-center lg:grid py-24 md:pb-32 sm:pt-16 lg:py-8 gap-16 md:gap-x-0 md:gap-y-24 relative isolate overflow-visible'
			}"
		>
			<template #title>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
					class="hidden lg:block text-primary text-5xl sm:text-7xl sm:text-5xl tracking-tight font-semibold lg:whitespace-nowrap will-change-transform"
				>
					{{ $t('business.hero.title1') }} <br class="hidden md:block">
					<span class="md:whitespace-nowrap">
						{{ $t('business.hero.title2') }}
					</span>
				</motion.div>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
					:initial="{ opacity: 0, transform: 'translateX(50px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.7 }"
					:in-view-options="{ once: true }"
					class="block text-4xl text-pretty tracking-tight font-semibold text-highlighted will-change-transform"
				>
					<div class="block text-4xl sm:text-6xl text-pretty tracking-tight font-semibold text-highlighted">
						{{ $t('business.hero.subtitle') }}
					</div>
				</motion.div>
			</template>
			<template #description>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 0.8, delay: 1.3 }"
					:in-view-options="{ once: true }"
					class="will-change-transform"
				>
					{{ $t('business.hero.description') }}
				</motion.div>
			</template>
			<template #links>
				<Motion
					as-child
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(25px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
					class="will-change-transform"
				>
					<UButton
						:id="GET_STARTED_BUTTON_ID"
						:label="$t('business.hero.cta')"
						to="#business-contact-form"
						size="xl"
						:ui="{ base: 'text-center block sm:inline-flex w-full sm:justify-center' }"
						@click="trackEvent($event, trackingLocation)"
						@mouseenter="trackEvent($event, trackingLocation)"
						@focus="trackEvent($event, trackingLocation)"
					/>
				</Motion>
			</template>
			<div class="max-w-[220px] sm:max-w-[320px] lg:max-w-[380px] xl:max-w-[406px] w-full lg:flex lg:items-end lg:justify-end lg:ml-auto -mt-8 lg:mt-42 xl:mt-16">
				<SvgImage
					id="svg-image-business-hero"
					provider="storyblok"
					fetchpriority="high"
					preload
					sizes="220px sm:320px lg:380px xl:406px"
					:image="{
						url: hero?.image?.filename,
						alt: hero?.image?.alt || 'Maverix Fiber-fast internet',
						offsetY: 0,
						offsetX: 0,
						height: 405,
						width: 405,
						densities: 'x1'
					}"
					:lines="[
						{
							rotation: -20,
							length: 140,
							direction: 'counter-clockwise',
							gap: 46
						}
					]"
					:circles="[
						{
							cx: 42,
							cy: 110,
							r: 114,
							fill: '#01629E'
						},
						{
							cx: 387.5,
							cy: 122,
							r: 18,
							fill: '#FF8A02'
						}
					]"
					:viewbox="{
						width: 406,
						height: 406
					}"
					class="h-full w-full lg:min-h-[350px] pb-6 xl:pb-24 lg:pb-0 sm:mt-8 lg:-mb-12 lg:mt-16 xl:mt-16 sm:mr-8 lg:mr-16 xl:mr-6 xl:-mb-24 shrink-0"
				/>
			</div>
		</UPageHero>
	</div>
</template>

<script setup lang="ts">
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { GET_STARTED_BUTTON_ID } from '@/constants/ga4/tracking-ids/pages/business'

import {
	motion, Motion
} from 'motion-v'

import type { HeroV2 } from '@/types/storyblok/hero'

defineProps({
	hero: {
		type: Object as PropType<HeroV2>,
		required: true
	},
	trackingLocation: {
		type: String,
		required: true
	}
})

const { trackEvent } = useGA4Tracking()
</script>
